/**
 * My figure: always at the top of the planet (the planet turns under me), on the tile I stand on,
 * beside anything tall standing in its middle, turning the way I walk, with a hop when I act.
 */
import { KIND } from '@bramvasterd/terrella-rules';
import * as THREE from 'three';

import { approach } from '@core/math';
import type { Game } from '@game/game';

import type { Planet } from '../planet/planet';
import type { PlanetPose } from '../planet/pose';
import type { Figures } from './figures';

/** Tiles with something standing in the middle: I stand beside it, not in it (so too on an extractor, or beside what I'm placing). */
const TALL = new Set<number>([KIND.TREE]);
const UP = new THREE.Vector3(0, 1, 0);
const HOP_MS = 420;

export interface Me {
  /** Place my figure, figure 0. */
  place(dt: number, now: number, pose: PlanetPose): void;
  /** A little hop, when I do something. */
  hop(now: number): void;
  /** Where my figure stands, in scene space. */
  readonly position: THREE.Vector3;
  /** How high the ground under me stands over the planet's radius, for the camera. */
  height(): number;
}

export function createMe(
  game: Game,
  figures: Figures,
  planet: Planet,
  color: () => string,
  reduced: boolean,
): Me {
  const position = new THREE.Vector3();
  const turn = new THREE.Quaternion();
  const tint = new THREE.Color();
  let height = 0;
  let aside = 0;
  let yaw = 0;
  let hopAt = -Infinity;
  return {
    position,
    height: () => height,
    hop(now) {
      hopAt = now;
    },
    place(dt, now, pose) {
      const s = game.store.get();
      const local = game.local;
      const here = s.here;
      const kind = here >= 0 && s.view ? (s.view.kind[here] ?? -1) : -1;
      const ground = here >= 0 ? planet.tileTop(here) : 0;
      height = reduced ? ground : approach(height, ground, 9, dt);
      const tall = TALL.has(kind) || s.placing !== null || (s.view?.machines.has(here) ?? false);
      const wantAside = tall ? 0.42 : 0;
      aside = reduced ? wantAside : approach(aside, wantAside, 8, dt);
      const off = aside * pose.scale;
      const top = (pose.radius + height - 0.005) * pose.scale;
      const hop = Math.max(0, 1 - (now - hopAt) / HOP_MS);
      position.set(off * 0.62, top + Math.sin(hop * Math.PI) * 0.32 * hop, off * 0.78);
      const heading = Math.atan2(local.heading[0] ?? 0, local.heading[2] ?? 1);
      const delta = ((heading - yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      yaw += delta * Math.min(1, dt * 10);
      turn.setFromAxisAngle(UP, yaw);
      const walking = local.moving > 0.2;
      const bob = reduced
        ? 0
        : walking
          ? Math.abs(Math.sin(now / 90)) * 0.045
          : Math.sin(now / 600) * 0.01;
      figures.set(0, position, turn, bob, tint.set(color()));
    },
  };
}
